import React, { useState, useEffect } from 'react';

/**
 * QuestionPersonality Component
 * Used for: Personality Scale (Karakter Pribadi, PAPI Kostick, dsb.)
 * Format: Clean Likert / Rating buttons without numbers or colored bias
 */
const QuestionPersonality = ({
    question,
    answer,
    onAnswer,
    disabled = false
}) => {
    const [selectedScale, setSelectedScale] = useState(answer || null);

    useEffect(() => {
        setSelectedScale(answer || null);
    }, [answer, question?.id]);

    const handleAnswer = (value) => {
        if (disabled) return;

        setSelectedScale(value);
        onAnswer(question.id, value);
    };

    // Helper to expand acronyms or numbers into clear descriptive text
    const clarifyText = (val, key) => {
        const str = String(val !== undefined && val !== null ? val : '').trim();
        const upper = str.toUpperCase();
        if (upper === 'STS' || upper === '1') return 'Sangat Tidak Sesuai';
        if (upper === 'TS' || upper === '2') return 'Tidak Sesuai';
        if (upper === 'N' || upper === '3') return 'Netral';
        if (upper === 'S' || upper === '4') return 'Sesuai';
        if (upper === 'SS' || upper === '5') return 'Sangat Sesuai';
        return str || `Pilihan ${key}`;
    };

    // Robustly resolve options list from question
    const getOptionsList = () => {
        let rawOptions = question.pilihan || (question.mapping && question.mapping.options) || question.options;

        if (typeof rawOptions === 'string') {
            try {
                rawOptions = JSON.parse(rawOptions);
            } catch (e) {
                rawOptions = null;
            }
        }

        const shuffleMap = question.mapping?.shuffle_map?.new_to_old || {};

        if (Array.isArray(rawOptions) && rawOptions.length > 0) {
            return rawOptions.map((opt, idx) => {
                let key = String(idx + 1);
                let text = '';
                if (typeof opt === 'object' && opt !== null) {
                    key = String(opt.key || opt.label || opt.value || idx + 1);
                    text = opt.text || opt.label || opt.value || '';
                } else {
                    text = String(opt);
                }
                const originalKey = shuffleMap[key] || key;
                return {
                    key: key,
                    originalKey: originalKey,
                    text: clarifyText(text, key),
                };
            });
        }

        if (typeof rawOptions === 'object' && rawOptions !== null && Object.keys(rawOptions).length > 0) {
            return Object.entries(rawOptions).map(([key, val]) => {
                let text = '';
                if (typeof val === 'object' && val !== null) {
                    text = val.text || val.label || val.value || '';
                } else {
                    text = String(val);
                }
                const originalKey = shuffleMap[key] || key;
                return {
                    key: String(key),
                    originalKey: originalKey,
                    text: clarifyText(text, key),
                };
            });
        }

        // Standard 5-point Likert fallback (clear descriptive labels, no numbers)
        return [
            { key: '1', originalKey: '1', text: 'Sangat Tidak Sesuai' },
            { key: '2', originalKey: '2', text: 'Tidak Sesuai' },
            { key: '3', originalKey: '3', text: 'Netral' },
            { key: '4', originalKey: '4', text: 'Sesuai' },
            { key: '5', originalKey: '5', text: 'Sangat Sesuai' }
        ];
    };

    const optionsList = getOptionsList();

    const isOptionSelected = (opt) => {
        const rawVal = selectedScale !== null && selectedScale !== undefined ? selectedScale : answer;
        if (rawVal === null || rawVal === undefined || rawVal === '') return false;

        const current = (Array.isArray(rawVal) ? String(rawVal[0] || '') : String(rawVal)).trim().toUpperCase();
        if (!current) return false;

        const optKey = String(opt.key || '').trim().toUpperCase();

        // 1. Direct match with option key
        if (current === optKey) {
            return true;
        }

        // 2. Only if no option in optionsList matches current by key, fallback to originalKey or text
        const anyKeyMatches = optionsList.some(o => String(o.key || '').trim().toUpperCase() === current);
        if (!anyKeyMatches) {
            const optOrig = String(opt.originalKey || '').trim().toUpperCase();
            const optText = String(opt.text || '').trim().toUpperCase();
            return (optOrig && current === optOrig) || (optText && current === optText);
        }

        return false;
    };

    return (
        <div className="question-personality">
            {/* Question Text */}
            <div className="mb-4">
                <div
                    style={{ fontSize: '1.2rem', fontWeight: 500 }}
                    dangerouslySetInnerHTML={{ __html: question.soal }}
                />
            </div>

            {/* Horizontal Scale Buttons (Neutral, clear text, no numbers, no rainbow colors) */}
            <div
                className="d-flex justify-content-center align-items-stretch flex-wrap"
                style={{
                    gap: '14px',
                    padding: '8px 0',
                    width: '100%'
                }}
            >
                {optionsList.map((opt) => {
                    const isSelected = isOptionSelected(opt);

                    return (
                        <button
                            key={opt.key}
                            onClick={() => handleAnswer(opt.key)}
                            disabled={disabled}
                            type="button"
                            className="btn"
                            style={{
                                fontSize: '0.95rem',
                                fontWeight: isSelected ? 700 : 500,
                                minWidth: '130px',
                                maxWidth: '220px',
                                flex: '1 1 140px',
                                minHeight: '64px',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                borderStyle: 'solid',
                                borderWidth: '2px',
                                borderColor: isSelected ? '#f56409' : '#cbd5e1',
                                backgroundColor: isSelected ? '#fff7ed' : '#ffffff',
                                color: isSelected ? '#c2410c' : '#334155',
                                boxShadow: isSelected
                                    ? '0 4px 12px rgba(245, 100, 9, 0.18)'
                                    : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.15s ease',
                                opacity: disabled ? 0.5 : 1,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                lineHeight: 1.35,
                                wordBreak: 'break-word',
                            }}
                        >
                            <span>{opt.text}</span>
                        </button>
                    );
                })}
            </div>

            {/* Helper Text */}
            <div className="mt-4 text-center">
                <small className="text-muted">
                    {selectedScale ? 'Pilihan tersimpan' : 'Pilih yang paling sesuai dengan kondisi Anda'}
                </small>
            </div>
        </div>
    );
};

export default QuestionPersonality;

